import { createFileRoute } from '@tanstack/react-router';
import * as v from 'valibot';
import { useLingui } from '@lingui/react/macro';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Suspense } from 'react';
import { trpc } from '@/lib/trpc';
import { Header } from './-components/header';
import { Footer } from './-components/footer';
import { Layout } from './-components/layout';
import { PageSeo } from './-components/page-seo';
import { slugs } from './solutions.meta';
import { Partners } from './-components/partners';
import { Hero } from './-components/solutions/hero';
import { SolutionTabs } from './-components/solutions/solution-tabs';
import { SOLUTION_KEYS } from './-components/solutions/solutions-data';
import { ValueProps } from './-components/solutions/value-props';
import { SuccessCases } from './-components/solutions/success-cases';
import { TechPartners } from './-components/solutions/tech-partners';

export const Route = createFileRoute('/new/solutions')({
	// Which channel tab to open. The footer names three of them directly, so the tab a visitor is sent to
	// has to survive the link — it cannot live in component state alone. `v.fallback` rather than a
	// failing parse: a stale or hand-typed `?solution=` should quietly open the first tab, not 404 a page
	// whose content is entirely static.
	validateSearch: v.object({
		solution: v.fallback(v.optional(v.picklist(SOLUTION_KEYS)), undefined),
	}),
	loader: ({ context }) => {
		// Warm up the two live feeds this page renders: the partner-merchant logos (global stats) and
		// the donation stream behind the hero counter. `prefetchQuery`, not `ensureQueryData` — see the
		// homepage loader for why a floating rejected promise here takes the server process down.
		context.queryClient.prefetchQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());
	},
	component: Component,
});

/** Real partner merchants (logo + live campaign, ordered by amount raised) — same source as the homepage. */
function PartnersSection() {
	const { data: stats } = useSuspenseQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());
	// No connector here: it is drawn to lead into the homepage's next section, not into the success stories.
	return <Partners merchants={stats.partnerMerchants} connector={false} />;
}

function Component() {
	const { t } = useLingui();

	return (
		<Layout>
			<Header />
			<PageSeo
				slugs={slugs}
				title={t`Solutions - Worldcoo`}
				description={t`Since 2013, at Worldcoo we build fundraising tools so that companies, their customers and their employees can finance social projects easily, comfortably and with full transparency.`}
			/>
			<Hero />
			<SolutionTabs />
			<ValueProps />
			<Suspense fallback={<div className="min-h-[600px]" />}>
				<PartnersSection />
			</Suspense>
			<SuccessCases />
			<TechPartners />
			<Footer cta="form" />
		</Layout>
	);
}
